import React, { useEffect, useState, useContext } from "react";
import { withRouter } from "react-router-dom";
import "./template-creation.scss";
import { GetProjectTemplateAPI } from "../../../../../services/api/project-template-api";
import {
  GetAllDepartmentsForTemplateScreen,
  GetUsersByDepartmentId,
} from "../../../../../services/api/department-api";
import {
  fetchDynamicFieldsDesigner,
  fetchObjectTypes,
} from "../../task-types/dynamic-fields/utils/admin-dynamic-fields-util";
import TemplateCreationFormNew from "./template-creation-form-new";
import TemplateCreationTopNew from "./template-creation-top-new";
import { LoginContext } from "../../../../../services/api/authentication-api";

const TemplateCreationPanelNew = (props) => {
  const isCreate = props.location.pathname.includes("/templates/create");
  const isView = props.location.pathname.includes("/templates/view");
  const isEdit =
    props.location.pathname.includes("/templates/edit") ||
    props.location.pathname.includes("/templates/view");
  const canCreate = props.location.state
    ? props.location.state.createPermission
    : true;
  const templateId = props.location.pathname.split("/")[4];

  const user = useContext(LoginContext);
  const rawDept = user?.currentUser?.department ?? null;
  const companyId = rawDept.Id ?? rawDept.CompanyID ?? rawDept.companyID ?? rawDept.CompanyId ?? 0;
  const company = rawDept
    ? {
        ...rawDept,
        Id: companyId,
      }
    : null;
  const isSuperAdmin = user?.currentUser?.profile?.isSuperAdminRole;

  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [users, setUsers] = useState([]);

  const [template, setTemplate] = useState({
    data: {
      Id: 0,
      CompanyId: companyId,
      ProjectType: "",
      Description: "",
      Days: 1,
      IsVisible: false,
      Reminder: "",
      ReminderOnDueDate: false,
      ReminderRecurring: "",
      ReminderRecurringTime: null,
      ReminderRecurringIntervalType: "",
      ReminderRecurringIntervalValue: 1,
      ReminderOneTime: false,
      ReminderOneTimeDaysBefore: 1,
      ReminderOneTimeTime: null,
      IsCreateNewUserTasks: false,
      IsCreateNewUserTasksCurrent: false,
      Owners: [],
      Members: [],
      DefaultMembership: false,
    },
    isLoading: true,
  });

  const [dynamicFields, setDynamicFields] = useState([]);
  const [alertConfig, setAlertConfig] = useState({
    showAlert: false,
    alertAction: "",
    alertMessage: "",
    alertVariant: "danger",
  });
  const [panelVisibility, setPanelVisibility] = useState({
    showCancelPanel: false,
    showConfirmPanel: false,
  });

  const LoadDepartments = async (companyId) => {
    try {
      let deptList = [];

      if (isSuperAdmin) {
        const searchQuery = {
          userId: user.userID,
          pageSize: 100000,
          pageNumber: 1,
          sortDesc: false,
          sortBy: "name",
          filters: [],
        };
        const result = await GetAllDepartmentsForTemplateScreen(searchQuery);
        if (result?.status === 200 || result?.response?.status === 200) {
          deptList = result.data.dataList || [];
        }
      }

      const myDept = { ...company, 
          CompanyId: companyId ?? company.companyID ?? company.CompanyId ?? company.CompanyID,
          Name: company.name ?? company.Name
       };
      setDepartments(deptList.length > 0 ? deptList : [myDept]);
      if (!isCreate)
      {
        setSelectedDepartment(deptList.find(d => d.Id === companyId) ?? myDept);
      } else {
        setSelectedDepartment(myDept);
      }
    } catch (err) {
      setDepartments([]);
      setSelectedDepartment(null);
      console.error(err);
    }
  };

  const LoadUsers = async (dept) => {
    if (!dept?.Id) return;
    try {
      const result = await GetUsersByDepartmentId(dept.Id);
      if (result?.status === 200 || result?.response?.status === 200) {
        setUsers(result.data || []);
      }
    } catch (err) {
      setUsers([]);
      console.error(err);
    }
  };

  const LoadDynamicFields = async () => {
    try {
      const dynamicFieldsPromise = await fetchDynamicFieldsDesigner(0, templateId);
      setDynamicFields(dynamicFieldsPromise);
    } catch (error) {
      setAlertConfig({
        showAlert: true,
        alertAction: "",
        alertMessage: error.toString(),
        alertVariant: "danger",
      });
    }
  };

  //We can load dynamic fields and object types inside the respective component
  const LoadObjectTypes = async () => {
    try {
      const objectTypes = await fetchObjectTypes();
      if (objectTypes?.status === 200 || objectTypes?.response?.status === 200) {
        return objectTypes;
      } else {
        throw new Error(objectTypes?.response?.data);
      }
    } catch (error) {
      console.log(error);
      setAlertConfig({
        showAlert: true,
        alertAction: "",
        alertMessage: error.toString(),
        alertVariant: "danger",
      });
    }
  };

  const LoadTemplate = async () => {
    try {
      let result = await GetProjectTemplateAPI(templateId);
      if (result?.status === 200 || result?.response?.status === 200) {
        LoadDynamicFields();
        LoadDepartments(result.data.CompanyId);
        setTemplate((prevState) => ({
          ...prevState,
          data: {
            ...prevState.data,
            ...result.data,
            Owners: result.data.Owners || [],
            Members: result.data.Members || [],
            DefaultMembership: !(result.data.Owners?.length === 0 && result.data.Members?.length === 0),
          },
          isLoading: false,
        }));
      } else {
        throw new Error(result?.response?.data);
      }
    } catch (error) {
      setAlertConfig({
        showAlert: true,
        alertAction: "Cannot Get Template",
        alertMessage: error.toString(),
        alertVariant: "danger",
      });
    }
  };

  useEffect(() => {
    if (isEdit) {
      LoadTemplate();
    } else {
      LoadDepartments();
    }
  }, []);

  useEffect(() => {
    if (selectedDepartment) {
      LoadUsers(selectedDepartment);
    }
  }, [selectedDepartment]);

  const updateOwners = (userIds) => {
    setTemplate((prev) => ({
      ...prev,
      data: {
        ...prev.data,
        Owners: userIds,
      },
    }));
  };

  const updateMembers = (userIds) => {
    setTemplate((prev) => ({
      ...prev,
      data: {
        ...prev.data,
        Members: userIds,
      },
    }));
  };

  const handleDepartmentChange = (deptIdStr) => {
    const dept = departments.find(d => String(d.Id) === String(deptIdStr));
    if (!dept) return;

    setSelectedDepartment(dept);
    setUsers([]);
    setTemplate(prev => ({
      ...prev,
      data: {
        ...prev.data,
        CompanyId: dept.Id,
        Owners: [],
        Members: [],
      },
    }));

  };

  return (
    <div
      className="template-creation-panel"
      onClick={() =>
        setAlertConfig((prevState) => ({
          ...prevState,
          showAlert: false,
        }))
      }
    >
      <TemplateCreationTopNew
        isEdit={isEdit}
        isView={isView}
        canCreate={canCreate}
        panelVisibility={panelVisibility}
        alertConfig={alertConfig}
        setPanelVisibility={setPanelVisibility}
        setAlertConfig={setAlertConfig}
        ProjectType={template.data?.ProjectType}
        templateId={templateId ? templateId : template.data?.Id}
      />

      <TemplateCreationFormNew
        template={template}
        isCreate={isCreate}
        setAlertConfig={setAlertConfig}
        setPanelVisibility={setPanelVisibility}
        dynamicFields={dynamicFields}
        isView={isView}
        isEdit={isEdit}
        objectTypes={() => LoadObjectTypes()}
        setDynamicFields={setDynamicFields}
        setTemplate={setTemplate}
        departments={departments}
        selectedDepartment={selectedDepartment}
        setSelectedDepartment={setSelectedDepartment}
        users={users}
        updateOwners={updateOwners}
        updateMembers={updateMembers}
        isSuperAdmin={isSuperAdmin}
        handleDepartmentChange={handleDepartmentChange}
      />
    </div>
  );
};

export default withRouter(TemplateCreationPanelNew);
